import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { Centered } from "./Login";
import { Field } from "./Setup";

/* The far end of the emailed reset link.
 *
 * This is the one screen in the app reachable with no session at all AND no
 * face picked first -- a link opened from an inbox, possibly on a device that
 * has never seen this app before. The token in the URL is the whole of its
 * authority; see core.auth.sign_password_reset for why it can only ever be
 * spent once.
 */
export function ResetPassword({
  token,
  onDone,
}: {
  token: string;
  onDone: () => void;
}) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready = next.length >= 8 && confirm === next;

  if (done) {
    return (
      <Centered>
        <h1 style={{ fontSize: 28, marginBottom: 6 }}>Password changed</h1>
        <p style={{ color: "var(--ink-2)", margin: "0 0 24px" }}>
          Sign in with your new password. Every other device signed in on
          this account has been signed out.
        </p>
        <button onClick={onDone} style={{ minWidth: 180 }}>
          Go to sign in
        </button>
      </Centered>
    );
  }

  return (
    <Centered>
      <h1 style={{ fontSize: 28, marginBottom: 6 }}>Choose a new password</h1>
      <p style={{ color: "var(--ink-2)", margin: "0 0 24px" }}>
        This link works once.
      </p>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            await api.post("/auth/password/reset", { token, newPassword: next });
            setDone(true);
          } catch (err) {
            setError(
              err instanceof ApiError
                ? err.message
                : "That didn't work. Try again.",
            );
          } finally {
            setBusy(false);
          }
        }}
        style={{ display: "grid", gap: 14, width: "100%", maxWidth: 320, textAlign: "left" }}
      >
        <Field label="New password" hint="At least 8 characters.">
          <input
            type="password"
            autoFocus
            value={next}
            autoComplete="new-password"
            onChange={(e) => setNext(e.target.value)}
            required
          />
        </Field>
        <Field label="New password again">
          <input
            type="password"
            value={confirm}
            autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </Field>

        {tooShort && (
          <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600, fontSize: 14 }}>
            That&rsquo;s too short. Use at least 8 characters.
          </p>
        )}
        {mismatch && (
          <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600, fontSize: 14 }}>
            Those two don&rsquo;t match.
          </p>
        )}
        {error && (
          <p role="alert" style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>
            {error}
          </p>
        )}

        <button type="submit" disabled={busy || !ready}>
          {busy ? "Saving…" : "Set new password"}
        </button>
      </form>
    </Centered>
  );
}
